import { Entry } from 'contentful';
import { BLOCKS } from '@contentful/rich-text-types';
import { IconName } from '@jbhi-fi/lanyard-ui/components/Icon';
import { Document } from '@contentful/rich-text-types';
import { CouponType } from '@jbhi-fi/components/src/types';

export enum ContentfulCarouselType {
  Product = 'Product',
  MiniProduct = 'Mini Product',
  Chart = 'Chart',
  Video = 'Video',
  Merch = 'Merch',
  ProductWithLeadBrand = 'Product with Lead Brand',
  ProductWithLeadImage = 'Product with Lead Image',
  ProductWithLeadVideo = 'Product with Lead Video',
  PromoCard = 'Promo card',
}

export enum ContentfulProductSource {
  Products = 'Products',
  Algolia = 'Algolia',
  GoogleRecAi = 'Google Rec AI',
  RecentlyViewed = 'Recently Viewed',
  Collection = 'Collection',
}

export enum ContentfulCarouselDisplayInfo {
  None = 'None',
  ReleaseDate = 'Release Date',
  Format = 'Format',
}

export enum ProgressBarThemeType {
  Default = 'Default (Black/Grey)',
  Dark = 'Dark (Yellow/Grey)',
}

export type ContentfulEntry<Fields, EntryId = string> = Omit<Entry<Fields>, 'toPlainObject' | 'update'> & {
  sys: { contentType: { sys: { id: EntryId | string } } };
};

export type ContentfulAssetFile = {
  url: string;
  fileName: string;
  contentType: string;
  details: {
    size: number;
    image?: {
      width: number;
      height: number;
    };
  };
};

export type ContentfulAsset = ContentfulEntry<{
  title: string;
  description: string;
  file: ContentfulAssetFile;
}>;

export type ContentfulRichText = {
  content: Array<any>;
  data: any;
  nodeType: BLOCKS.DOCUMENT;
};

export type ContentfulNavDividerAndHeading = ContentfulEntry<{
  heading: string;
  shouldDisplayHeading: boolean;
  mainCollection?: ContentfulCollection;
}>;

export type ContentfulNavMenu = ContentfulEntry<{
  name: string;
  heading: string;
  isHeadingBold: boolean;
  url?: string;
  openInNewWindow?: boolean;
  mainCollection?: ContentfulCollection;
  topNavMenuItems?: Array<
    ContentfulNavMenu | ContentfulNavDividerAndHeading | ContentfulCollection | ContentfulGenericPage
  >;
  navMenuItems?: Array<
    ContentfulNavMenu | ContentfulNavDividerAndHeading | ContentfulCollection | ContentfulGenericPage
  >;
  icon?: ContentfulIcon;
  logo?: ContentfulAsset;
}>;

export type ContentfulNav = ContentfulEntry<{
  name: string;
  navMenuItems: Array<ContentfulNavMenu>;
  mobileNavMenuItems?: Array<ContentfulNavMenu>;
  type: 'Header Nav' | 'Footer Nav' | 'Socials' | 'Payment Providers' | 'Footer Legals';
}>;

export type ContentfulGenericPage = ContentfulEntry<
  {
    heading: string;
    parentPage?: ContentfulSubCategoryPage | ContentfulCategoryLandingPage | ContentfulContentHubHomepage;
    breadcrumbParent?: ContentfulCollection | ContentfulGenericPage;
    heroTheme?: ContentfulTheme;
    heroLogo?: ContentfulAsset;
    heroSection?: 'Icon size' | 'Half size' | 'Content size' | 'Full size';
    mainBody: ContentfulRichText;
    preFooterSection?: ContentfulMultiColumnBlock;
    urlSlug?: string;
    seoPageTitle?: string;
    seoPageDescription?: string;
    seoContentA?: string;
    seoContentB?: string;
    metaRobotsTag: boolean;
    searchDisplayText?: string;
    searchImage?: ContentfulAsset;
    searchKeywords?: Array<string>;
    searchResultRanking?: number;
    subDirectory?: 'Pages' | 'Blogs';
    publishedDate?: string;
  },
  'genericPage'
>;

export type ContentfulIcon = ContentfulEntry<{
  name: IconName;
}>;

export type ContentfulNavItem = ContentfulEntry<
  {
    name: string;
    handle: string;
    url?: string;
    icon?: ContentfulIcon;
    style?: ['bold'] | [];
    openInNewWindow?: boolean;
    browseNavImage?: ContentfulAsset;
  },
  'navItem'
>;

export type ContentfulBrowseGroup = ContentfulEntry<
  {
    name: string;
    handle: string;
    browseItems: Array<ContentfulCollection | ContentfulNavItem>;
  },
  'browseGroup'
>;

export type ContentfulCollection = ContentfulEntry<
  {
    title: string;
    handle: string;
    redirectUrl?: string;
    allowedAccess?: 'Everyone' | 'Perks members';
    template: 'Enhanced' | 'Curated' | 'Catalogue' | 'Seller' | 'MerchStudio';
    algoliaFilterQuery?: string;
    searchText?: string;
    cataloguedSkus?: Array<string>;
    defaultSortMethod?:
      | 'price_asc'
      | 'price_desc'
      | 'published_at_asc'
      | 'published_at_desc'
      | 'title_asc'
      | 'title_desc'
      | 'rating_desc';
    heroProducts?: Array<string>;
    facetFilters?: Array<string>;
    curatedCollections?: Array<ContentfulCollection>;
    icon?: ContentfulIcon;
    nestedCollection?: boolean;
    parentCollection?: ContentfulCollection;
    ancestorCollections?: Array<ContentfulCollection>;
    visibleInMainNav?: boolean;
    customNav?: Array<any>;
    browseNav?: Array<ContentfulCollection | ContentfulNavItem | ContentfulBrowseGroup>;
    seoPageTitle?: string;
    seoPageDescription?: string;
    seoContentA?: string;
    seoContentB?: string;
    preezieBanner?: ContentfulPreezieBanner;
    collectionImage?: ContentfulAsset;
    browseNavVisual?: 'Image' | 'Button';
    imsCategoryId?: number;
    attachBundle?: ContentfulAttachBundle;
    attachInfo?: ContentfulAttachInfo;
    splitTiles?: boolean;
  },
  'smartCollection'
>;

export type ContentfulMetafieldCollection = {
  title: string;
  handle: string;
  template:
    | 'curated'
    | 'enhanced'
    | 'catalogue'
    | 'seller'
    | 'merchstudio'
    | 'default' /* default = enhanced */
    | string;
  browseNavVisual?: 'Image' | 'Button';
  filters?: string;
  searchFilters?: string;
  seoTextA?: string;
  seoTextB?: string;
  sortMethod?: string;
  redirect?: string;
  query?: string;
  facets?: Array<{ facet: string; type: 'refinementList' | string }>;
  browse?: Array<{ handle?: string; path?: string; title?: string; name?: string; parentHandle?: string }>;
  breadcrumbs?: Array<{ handle: string; name: string; position: number; url: string }>;
  curated_collections?: Array<any>;
  sellerId?: string;
  ads?: {
    carousel_banners?: Array<any>;
    leaderboards?: Array<{
      brand?: string;
      desktopAssetURL?: string;
      destinationURL?: string;
      fileName?: string;
      mobileAssetURL?: string;
      pos?: string;
    }>;
    left_wing?: Array<any>;
    right_wing?: Array<any>;
  };
};

export type ContentfulButton = ContentfulEntry<{
  name: string;
  label: string;
  type: 'Primary' | 'Secondary' | 'Green' | 'Link';
  url: string;
  icon: ContentfulIcon;
  alignment: 'Centred' | 'Left' | 'Right';
  shouldOpenInNewWindow?: boolean;
}>;

export type ContentfulButtonBlock = ContentfulEntry<{
  name: string;
  alignment: 'Left' | 'Centred';
  buttons: Array<ContentfulButton>;
  divider?: string;
}>;

export type ContentfulMultiColumnEntry = ContentfulEntry<{
  heading: string;
  headingSize: 'Small' | 'Medium' | 'Large';
  subText: ContentfulRichText;
  image: ContentfulAsset;
  imageSize: 'Third' | 'Half' | 'Full' | 'Thumbnail';
  button: ContentfulButton;
}>;

export type ContentfulMultiColumnBlock = ContentfulEntry<{
  entries: Array<ContentfulMultiColumnEntry>;
  contentAlignment: 'Left' | 'Centred';
  desktopColumns: 2 | 3 | 4 | 5;
  mobileFormat: 'Stack' | 'Double' | 'Carousel';
  themeColour?: ContentfulTheme;
  heading?: string;
}>;

export type ListingBlockType = 'Image grid' | 'Icons' | 'Image cards' | 'Image boxes' | 'Image thumbnails';

export type ContentfulListingBlock = ContentfulEntry<{
  heading: string;
  subText?: string;
  entries: Array<ContentfulListingEntry>;
  type: ListingBlockType;
  ctaButton?: ContentfulButton;
  theme?: ContentfulTheme;
  isTwoRows?: boolean;
}>;

export type ContentfulListingEntry = ContentfulEntry<{
  heading: string;
  url: string;
  image?: ContentfulAsset;
  icon?: ContentfulIcon;
  collection?: ContentfulCollection;
  page?: ContentfulGenericPage;
}>;

export type ContentfulTheme = ContentfulEntry<{
  backgroundColourHex: string;
  textColourHex: string;
  headingColourHex: string;
  headingBackgroundColourHex: string;
  backgroundImage: ContentfulAsset;
  icon?: ContentfulIcon;
}>;

export type ContentfulCountdownBar = ContentfulEntry<{
  name: string;
  heading: string;
  text?: string;
  theme: ContentfulTheme;
  countdownTo: string;
  url: string;
  state?: Array<string>;
  paths?: string;
  type?: string;
  showAgainAfter?: number;
  icon?: ContentfulIcon;
}>;

export type ContentfulContactBlock = ContentfulEntry<{
  name: string;
  heading: string;
  subText?: string;
  chatText: string;
  chatUrl: string;
  hideCallUs: boolean;
  callText: string;
  callUrl: string;
  themeColourAndIcon?: ContentfulTheme;
  margin?: 'top' | 'bottom' | 'left' | 'right';
  disclaimerText?: string;
}>;

export type ContentfulTag = {
  fields: {
    heading: string;
  };
};

export type ContentfulCategoryLandingPage = ContentfulEntry<{
  heading: string;
  seoPageTitle: string;
  urlSlug: string;
  seoMetaDescription: string;
  heroTheme: ContentfulTheme;
  logo?: ContentfulAsset;
  heroBlock: ContentfulHeroBlock;
  launchTiles?: Array<ContentfulLaunchTile>;
  launchTileHoverTheme?: ContentfulTheme;
  miniBanner?: ContentfulHeroCta;
  sections: Array<ContentfulSection>;
}>;

export type ContentfulSubCategoryPage = ContentfulEntry<{
  heading: string;
  urlSlug: string;
  categoryPage?: ContentfulCategoryLandingPage;
  featuredBanner?: ContentfulFeaturedBanner;
  pinnedArticles?: Array<ContentfulArticle | ContentfulCompetition>;
  pinnedArticlesHeading?: string;
  themeColour?: ContentfulTheme;
  seoPageTitle?: string;
  seoMetaDescription?: string;
}>;

export type ContentfulSection = ContentfulEntry<{
  heading: string;
  url?: string;
  shouldOpenInNewWindow?: boolean;
  hideHeading?: boolean;
  theme?: ContentfulTheme;
  sectionContent: Array<
    | ContentfulCarousel
    | ContentfulContentBanner
    | ContentfulCardGrid
    | ContentfulHeroBlock
    | ContentfulListingBlock
    | ContentfulProductBlock
    | ContentfulMultiColumnBlock
    | ContentfulSideBySide
  >;
}>;

export type ContentfulHeroBlock = ContentfulEntry<{
  heading: string;
  cards: Array<ContentfulArticle | ContentfulCompetition | ContentfulHeroCta>;
  mobilePresentation: 'Stack' | 'Slide';
}>;

export type ContentfulHeroCta = ContentfulEntry<{
  heading: string;
  subText: string;
  url: string;
  shouldOpenInNewWindow?: boolean;
  theme: ContentfulTheme;
  icon?: ContentfulIcon;
  image?: ContentfulAsset;
}>;

export type ContentfulCardGrid = ContentfulEntry<{
  cardGridName: string;
  fromLastXDays: number;
  cardCount: number;
  featuredBanner?: ContentfulFeaturedBanner;
  parentPage: ContentfulSubCategoryPage | ContentfulCategoryLandingPage;
}>;

export type ContentfulArticle = ContentfulEntry<{
  author: ContentfulAuthor;
  heading: string;
  heroImage: ContentfulAsset;
  heroVideo?: ContentfulYoutube;
  intro: string;
  mainBody: ContentfulRichText;
  parentPage: ContentfulSubCategoryPage;
  tags: Array<ContentfulTag>;
  urlSlug: string;
  seoPageTitle?: string;
  seoMetaDescription?: string;
  publishedDate?: string;
}>;

export type ContentfulCompetition = ContentfulEntry<{
  heading: string;
  parentPage: ContentfulSubCategoryPage;
  stackMemberExclusive: boolean;
  perksMemberExclusive: boolean;
  heroImage: ContentfulAsset;
  intro?: string;
  mainBody: ContentfulRichText;
  startDate: string;
  endDate: string;
  winnerAnnouncementDate: string;
  termsConditions: ContentfulAsset;
  winners?: string;
  seoPageTitle?: string;
  urlSlug: string;
  seoMetaDescription?: string;
  tags: Array<ContentfulTag>;
}>;

export type ContentfulLaunchTile = ContentfulEntry<{
  buttonLabel: string;
  buttonUrl: string;
  shouldOpenInNewWindow?: boolean;
  icon: ContentfulIcon;
}>;

export type ContentfulAuthor = ContentfulEntry<{
  name: string;
  title: string;
  profilePicture?: ContentfulAsset;
}>;

export type ContentfulYoutube = ContentfulEntry<{
  name: string;
  recommendedVideos: boolean;
  url: string;
}>;

export type ContentfulFeaturedBanner = ContentfulEntry<{
  name: string;
  bannerImage: ContentfulAsset;
  url: string;
  theme: ContentfulTheme;
  productTag?: string;
  productTagSubText?: string;
  shouldOpenInNewWindow?: boolean;
}>;

export type ContentfulContentBanner = ContentfulEntry<{
  heading: string;
  subText: string;
  heroImage: ContentfulAsset;
  button: ContentfulButton;
  categoryColour: ContentfulTheme;
  theme: ContentfulTheme;
}>;

export type ContentfulCarousel = ContentfulEntry<{
  type: ContentfulCarouselType;
  displayInfo: ContentfulCarouselDisplayInfo;
  products?: Array<string>; // Array of product ID's to be fetched from algolia
  algoliaFilterQuery?: string; // Algolia filter to return a 'collection' of products
  articles?: Array<ContentfulArticle>; // Array of articles (unknown if linked id or full article)
  promoCards?: Array<ContentfulPromoCard>;
  heading?: string;
  image?: ContentfulAsset;
  mobileVideoCoverImage?: ContentfulAsset;
  youTubeLeadVideo?: ContentfulYoutube;
  subText?: string;
  buttonType?: 'Link' | 'Green' | 'Secondary' | 'Primary' | 'None';
  buttonLabel?: string;
  buttonUrl?: string;
  productSource: ContentfulProductSource;
  googleRecAiModel?: string;
  googleRecAiNumberOfProducts?: number;
  collection?: ContentfulCollection;
  shouldLoadOnScroll?: boolean;
}>;

export type ContentfulProductGrid = ContentfulEntry<{
  heading: string;
  productSource: ContentfulProductSource;
  products?: Array<string>;
  algoliaFilterQuery?: string;
  collection?: ContentfulCollection;
}>;

export type ContentfulCompetitionGrid = ContentfulEntry<{
  name: string;
  parentPage: ContentfulSubCategoryPage | ContentfulCategoryLandingPage;
  openedOrClosed: boolean; // true = open, false = closed
  closedWithinXDaysAgo?: number;
  closingInXDays?: number;
}>;

export type ContentfulProductSummary = ContentfulEntry<{
  heading: string;
  description: ContentfulRichText;
  product: string; // product ID
}>;

export type ContentfulContentWrapper = ContentfulEntry<{
  name: string;
  heroImage: ContentfulAsset;
  featuredProducts: Array<ContentfulProductSummary>;
}>;

export type ContentfulFormAssemblyForm = ContentfulEntry<{
  formName: string;
  formAssemblyHtml: string;
}>;

export type ContentfulContentHubHomepage = ContentfulEntry<{
  heading: string;
  subText: string;
  seoPageTitle: string;
  urlSlug: string;
  seoMetaDescription: string;
  heroTheme: ContentfulTheme;
  logo?: ContentfulAsset;
  heroBlock: ContentfulHeroBlock;
  launchTiles?: Array<ContentfulLaunchTile>;
  launchTileHoverTheme?: ContentfulTheme;
  miniBanner?: ContentfulHeroCta;
  sections: Array<ContentfulSection>;
}>;

export type ContentfulVideoTimestamp = ContentfulEntry<{
  heading: string;
  timestamp: number;
  products?: Array<string>;
  algoliaQuery?: string;
  url?: string;
  productSource?: 'Products' | 'Algolia' | 'Collection';
  collection?: ContentfulCollection;
}>;

export type ContentfulVideoTimeline = ContentfulEntry<{
  heading: string;
  videoTimestamps: Array<ContentfulVideoTimestamp>;
}>;

export enum ContentPrimaryCategory {
  moviesAndTv = 'Movies & TV',
  music = 'Music',
  games = 'Gaming',
  tech = 'Tech',
  other = 'Other',
  musicNews = 'Music News',
  albumReview = 'Album Reviews',
  movieNews = 'Movie & TV news',
}

export enum ContentSubCategory {
  news = 'news',
  review = 'review',
  interview = 'interview',
  competition = 'competition',
  article = 'article',
  other = 'other',
}

export type HomepageSection = ContentfulListingBlock | ContentfulEntry<any>;

export type ContentfulHomepage = ContentfulEntry<{
  name: string;
  countdownBar: ContentfulCountdownBar;
  saleTakeover?: ContentfulTakeoverBlock;
  sections: Array<HomepageSection>;
  isAbTest: boolean;
  urlSlug: string;
}>;

export type ContentfulPreezieBanner = ContentfulEntry<{
  guidekey: string;
  placement: string;
  title?: string;
  heading?: string;
  subText?: ContentfulRichText;
  icon?: ContentfulAsset;
}>;

export enum EntryType {
  article = 'article',
  categoryLanding = 'landingPage',
  contentHubHomepage = 'contentHubHomepage',
  competition = 'competition',
  subCategory = 'subLandingPage',
  genericPage = 'genericPage',
  homepage = 'homepage',
}

export type ContentfulArticleBlock = ContentfulEntry<{
  name?: string;
  heading: string;
  headingLogo?: ContentfulAsset;
  subText?: string;
  heroBlocks: Array<ContentfulHeroBlock>;
  button?: ContentfulButton;
  theme: ContentfulTheme;
  miniBanner?: ContentfulHeroCta;
}>;

export type ContentfulPromoCard = ContentfulEntry<{
  name: string;
  loginState?: 'Guest only' | 'Logged in only';
  mobileImage: ContentfulAsset;
  url: string;
  isExternal?: boolean;
}>;

export type ContentfulProductBlock = ContentfulEntry<{
  name: string;
  heading: string;
  productCarousel: Array<ContentfulCarousel>;
  theme?: ContentfulTheme;
}>;

export type ContentfulCampaignRuleset = ContentfulEntry<{
  rulesetName: string;
  handles: Array<string>;
  startTime: string;
  carouselPriority?: string;
  leaderboardPriority?: number;
}>;

export type ContentfulCampaign = ContentfulEntry<{
  name: string;
  doNotDelete: boolean;
  carouselBanners: Array<ContentfulCarouselBanner | ContentfulPercentOffBanner | ContentfulProductBanner>;
  leaderboards: Array<ContentfulLeaderboardBanner>;
  leftWing: ContentfulWingsBanner;
  rightWing: ContentfulWingsBanner;
  campaignRulesets: ContentfulCampaignRuleset;
}>;

export type ContentfulLeaderboardBanner = ContentfulEntry<{
  name: string;
  doNotDelete: boolean;
  urllink: string;
  desktopLeaderboard?: ContentfulAsset;
  mobileLeaderboard?: ContentfulAsset;
  isExternal?: boolean;
}>;

export type ContentfulCarouselBanner = ContentfulEntry<{
  name: string;
  doNotDelete?: boolean;
  urllink: string;
  isExternal?: boolean;
  hasBorder: boolean;
  dynamicImageUrls: boolean;
  desktopHeroBanner?: ContentfulAsset;
  mobileHeroBanner?: ContentfulAsset;
  desktopImage?: ContentfulAsset;
  desktopHeroBannerUrl?: string;
  mobileHeroBannerUrl?: string;
  backgroundColour?: ContentfulTheme;
  progressBarTheme: ProgressBarThemeType;
  backgroundImage: ContentfulAsset | undefined;
}>;

export type ContentfulWingsBanner = ContentfulEntry<{
  bannerName?: string;
  doNotDelete?: boolean;
  urllink?: string;
  desktopImage: ContentfulAsset;
  brand: string;
  isExternal?: boolean;
}>;

export type ContentfulSideBySide = ContentfulEntry<{
  name: string;
  heading?: string;
  body?: ContentfulRichText;
  tag?: string;
  assetPosition: 'Left' | 'Right';
  imageOrVideo?: ContentfulAsset;
  youTube?: ContentfulYoutube;
  ctaLink?: ContentfulButton;
}>;

export type ContentfulTakeoverBlock = ContentfulEntry<{
  name: string;
  heroBanner?: ContentfulCarouselBanner;
  displayCarouselHeading: boolean;
  carousel: ContentfulCarousel;
}>;

export type ContentfulAnchor = ContentfulEntry<
  {
    heading: string;
    showAs: 'hide' | 'h2' | 'h3' | 'h4';
    theme?: ContentfulTheme;
  },
  'anchor'
>;
export type ContentfulTooltip = ContentfulEntry<
  {
    name: string;
    place:
      | 'top'
      | 'top-start'
      | 'top-end'
      | 'right'
      | 'right-start'
      | 'right-end'
      | 'bottom'
      | 'bottom-start'
      | 'bottom-end'
      | 'left'
      | 'left-start'
      | 'left-end';
    enabled: boolean;
    message: string;
    elementClass?: string;
    borderRadius?: boolean;
    showByDefault?: boolean;
    showOn?: string;
    showFor?: string;
    showOnHover?: boolean;
    closeOnlyOnClick?: boolean;
    theme?: ContentfulTheme;
    hideCloseButton?: boolean;
    accurateBorderRadius?: string;
    shouldHideArrow?: boolean;
    isAnimationEnabled?: boolean;
    isTooltipOut?: boolean;
    transition?: 'from-top' | 'from-bottom' | 'from-left' | 'from-right';
  },
  'tooltip'
>;

export type ContentfulTableOfContents = ContentfulEntry<
  {
    name: string;
    heading: string;
    anchors: Array<ContentfulAnchor>;
  },
  'tableOfContents'
>;
export type ContentfulAttachBundle = ContentfulEntry<{
  name: string;
  heading: string;
  subtext?: ContentfulRichText;
  disclaimer?: ContentfulRichText;
}>;

export type ContentfulAttachInfo = ContentfulEntry<{
  name: string;
  heading: string;
  subtext?: ContentfulRichText;
}>;

export type ContentfulCallout = ContentfulEntry<{
  name: string;
  heading: string;
  type: 'Standard' | 'Info' | 'Warning' | 'Success' | 'Error';
  body: ContentfulRichText;
  secondaryImage?: ContentfulAsset;
  leftAnchorImage?: ContentfulAsset;
}>;

export type ContentfulPageListing = ContentfulEntry<
  {
    name: string;
    pageTypeToQuery: 'Collections'; // 'Articles' | 'Collections' | 'Generic Pages'; Possible plans to handle more types in the future.
    collectionType?: 'Seller'; //'Enhanced' | 'Curated' | 'Catalogue' | 'Seller';
    sorting: 'Alphabetical (Ascending)' | 'Alphabetical (Descending)';
  },
  'pageListing'
>;

export type ContentfulSnackbar = ContentfulEntry<{
  name: string;
  countdownTo?: string;
  theme?: ContentfulTheme;
  url?: string;
  text?: ContentfulRichText;
  heading?: string;
  state?: Array<string>;
  paths?: Array<string>;
  type: string;
  showAgainAfter?: number;
  icon?: IconName;
  priority?: number;
  dismissible?: boolean;
}>;

export type ContentfulCurrentActiveEntries = ContentfulEntry<
  {
    name: string;
    homepage?: ContentfulHomepage;
    homepagePerks?: ContentfulHomepage;
    headerNav: ContentfulNav;
    footerNav: ContentfulNav;
    footerSocials: ContentfulNav;
    footerPayments: ContentfulNav;
    footerLegals: ContentfulNav;
    snackbar?: Array<ContentfulSnackbar>;
    pdpAskForADeal: ContentfulContactBlock;
  },
  'currentActiveHomepage'
>;

export type ContentfulCoupon = ContentfulEntry<
  {
    couponType: CouponType;
    name: string;
    handle: string;
    couponExternalId: Array<string>;
    heroImage: ContentfulAsset;
    validityPeriod: string;
    headingOne?: string;
    headingTwo?: string;
    headingThree?: string;
    contentOne?: Document;
    contentTwo?: Document;
    contentThree?: Document;
  },
  'coupon'
>;

export type ContentfulSearchContent = ContentfulEntry<
  {
    title: string;
    keywords: Array<string>;
    handle?: string;
    order: number;
    image?: ContentfulAsset;
    destination?: ContentfulEntry<any>;
    url?: string;
  },
  'searchContent'
>;

export type ContentfulSupportArticle = ContentfulEntry<
  {
    heading: string;
    mainBody?: ContentfulRichText;
    supportCategory: ContentfulSupportCategory;
    urlSlug: string;
    seoPageDescription?: string;
  },
  'supportArticle'
>;

export type ContentfulAccordionEntry = ContentfulEntry<
  {
    heading: string;
    mainBody: ContentfulRichText;
  },
  'accordionEntry'
>;

export type ContentfulAccordion = ContentfulEntry<
  {
    name: string;
    accordionEntries: Array<ContentfulAccordionEntry>;
  },
  'accordion'
>;

export type ContentfulSupportCategory = ContentfulEntry<
  {
    heading: string;
    parentCategory?: ContentfulSupportCategory;
    icon: IconName;
    urlSlug: string;
  },
  'supportCategory'
>;

export type ContentfulSupportLandingPage = ContentfulEntry<
  {
    name: string;
    supportCategories: Array<ContentfulSupportCategory>;
    supportSections: Array<ContentfulEntry<any>>;
  },
  'supportLandingPage'
>;

export type ContentfulPercentOffBanner = ContentfulEntry<{
  name: string;
  percentOffAmount: string;
  categoryText: string;
  brandLogos: Array<string>;
  disclaimer: string;
  url: string;
  endDate: string;
  displayShopNowButton: boolean;
  theme: ContentfulTheme;
  backgroundImage: ContentfulAsset | undefined;
}>;

export type ContentfulProductBanner = ContentfulEntry<{
  name: string;
  heading: string;
  url?: string;
  overallEndDate?: string;
  theme: ContentfulTheme;
  product1: Array<string> & { length: 1 };
  endDate1?: string;
  tagType1: string;
  badgeStyle1?: string;
  badge1?: string;
  isEA1?: boolean;
  product2?: Array<string> & { length: 1 };
  endDate2?: string;
  tagType2?: string;
  badgeStyle2?: string;
  badge2?: string;
  isEA2?: boolean;
  product3?: Array<string> & { length: 1 };
  endDate3?: string;
  tagType3?: string;
  badgeStyle3?: string;
  badge3?: string;
  isEA3?: boolean;
  backgroundImage: ContentfulAsset | undefined;
}>;

export type ContentfulSupplierBanner = ContentfulEntry<{
  name: string;
  desktopImage: ContentfulAsset;
  mobileImage: ContentfulAsset;
  overallEndDate?: string;
  textAndButtonColourBlack: boolean;
  showNewAtJbBadge: boolean;
  show1ProductInBoxWithImage: boolean;
  product1: Array<string> & { length: 1 };
  endDate1?: string;
  tagType?: string;
  badgeStyle?: string;
  badge?: string;
  showEaInPriceBox1?: boolean;
  product2?: Array<string> & { length: 1 };
  endDate2?: string;
  showEaInPriceBox2?: boolean;
}>;
